import {
  ActionIcon,
  Anchor,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Table,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTimeAgo } from '@commons/timeAgo';
import { useIntl } from 'react-intl';
import routes from '@commons/routes';
import { IconEdit, IconSettings, IconTrash } from '@tabler/icons-react';
import { DeleteModal } from './Modals/DeleteModal';
import { FilterField } from './types/FilterField';
import { useAppDispatch, useAppSelector } from '@store';
import { fetchFiltersWithNotification } from '@store/slices/filtersSlice';

export const UserFilters = () => {
  const timeAgo = useTimeAgo();
  const { username } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.filters.filters);
  const isLoadingFilters = useAppSelector((state) => state.filters.isLoading);
  const intl = useIntl();
  const [deleteFilter, setDeleteFilter] = useState<any>(null);

  const rows = filters.map((filter: any) => (
    <Table.Tr key={filter.name}>
      <Table.Td>
        <Anchor
          fz="sm"
          href={routes.routes.filters.view(username as string, filter.id)}
          target="_blank"
        >
          {filter.name}
        </Anchor>
      </Table.Td>
      <Table.Td>
        {filter.fields.map((field: FilterField) => field.label).join(', ')}
      </Table.Td>
      <Table.Td ta="center">
        <Menu shadow="md" withArrow>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              aria-label={intl.formatMessage({ id: 'actions' })}
              color="gray"
            >
              <IconSettings size={18} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={18} />}
              onClick={() =>
                navigate(
                  routes.routes.filters.edit(username as string, filter.id)
                )
              }
            >
              {intl.formatMessage({ id: 'edit' })}
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={18} />}
              onClick={() => setDeleteFilter(filter)}
            >
              {intl.formatMessage({ id: 'delete' })}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
      <Table.Td ta="center">{filter.category}</Table.Td>
      <Table.Td ta="center">
        {timeAgo.format(new Date(filter.updatedAt))}
      </Table.Td>
    </Table.Tr>
  ));

  if (isLoadingFilters) {
    return (
      <Container size="xl">
        <Center h={250}>
          <Loader />
        </Center>
      </Container>
    );
  }

  return (
    <Container my="md" size="xl">
      <Group justify="space-between" align="center" mb="md">
        <Title order={4}>{intl.formatMessage({ id: 'filters' })}</Title>

        <Button
          id="add-new-filter-button"
          variant="accent"
          onClick={() => {
            navigate(routes.routes.filters.new(username as string));
          }}
        >
          {intl.formatMessage({ id: 'addFilter' })}
        </Button>
      </Group>

      <Table.ScrollContainer minWidth={1200}>
        <Table
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
          layout="auto"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{intl.formatMessage({ id: 'name' })}</Table.Th>
              <Table.Th>{intl.formatMessage({ id: 'fields' })}</Table.Th>
              <Table.Th ta="center" w={150}>
                {intl.formatMessage({ id: 'actions' })}
              </Table.Th>
              <Table.Th ta="center" w={150}>
                {intl.formatMessage({ id: 'category' })}
              </Table.Th>
              <Table.Th ta="center" w={150}>
                {intl.formatMessage({ id: 'updatedAt' })}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {deleteFilter && (
        <DeleteModal
          filter={deleteFilter}
          onClose={() => {
            setDeleteFilter(null);
            if (username) {
              dispatch(fetchFiltersWithNotification(username));
            }
          }}
        />
      )}
    </Container>
  );
};
